/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Typography, useMediaQuery, useTheme } from "__barrel_optimize__?names=Box,Button,Typography,useMediaQuery,useTheme!=!@mui/material";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useTranslation } from "../../../i18n/client";
import { tokens } from "../../application/theme";
import PricingCard from "./PricingCard";
import { FREE_PLAN, pricingPlans } from "./plans";
function isCurrentPlanDuration(currentDuration, planDuration) {
    if (!currentDuration) return false;
    return currentDuration.toLowerCase().includes(planDuration);
}
export default function PricingSection(param) {
    let { mode, currentPlan, currentDuration, isExpired = false, onPlanClick, onManagePlanClick } = param;
    const [isPerMonth, setIsPerMonth] = useState(true);
    const params = useParams();
    const { t } = useTranslation(params ? params.lng : "en", "pricing-page");
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
    const effectiveCurrentPlan = isExpired ? FREE_PLAN : currentPlan;
    const showCurrentPlan = mode === "account";
    const showManagePlanButton = mode === "account" && effectiveCurrentPlan !== FREE_PLAN && onManagePlanClick;
    useEffect(()=>{
        if (!currentDuration) return;
        setIsPerMonth(!currentDuration.toLowerCase().includes("year"));
    }, [
        currentDuration
    ]);
    const visiblePlans = pricingPlans.filter((plan)=>isPerMonth ? plan.duration === "month" : plan.duration === "year" && plan.planName !== FREE_PLAN).filter((plan)=>{
        if (mode !== "account") return true;
        return effectiveCurrentPlan === FREE_PLAN || plan.planName !== FREE_PLAN;
    });
    return /*#__PURE__*/ _jsxs(Box, {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "20px",
        sx: {
            width: "100%"
        },
        children: [
            showManagePlanButton && /*#__PURE__*/ _jsx(Box, {
                sx: {
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                    maxWidth: "1100px",
                    px: "20px",
                    pt: "33px",
                    boxSizing: "border-box"
                },
                children: /*#__PURE__*/ _jsx(Button, {
                    sx: {
                        background: colors.button.main,
                        borderRadius: "12px",
                        color: "#fff",
                        fontSize: "18px",
                        fontWeight: "600",
                        padding: "10px 24px",
                        whiteSpace: "nowrap",
                        "&:hover": {
                            background: colors.button.mainOnHower
                        }
                    },
                    onClick: onManagePlanClick,
                    children: t("modifyPlan")
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                fontSize: "32px",
                fontWeight: "bold",
                textAlign: "center",
                children: t("pricingHeading")
            }),
            /*#__PURE__*/ _jsxs(Box, {
                sx: {
                    maxWidth: "1100px",
                    width: "100%",
                    px: "20px",
                    boxSizing: "border-box"
                },
                textAlign: "center",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        fontSize: "16px",
                        fontWeight: "500",
                        children: t("pricingDescriptionIntro")
                    }),
                    /*#__PURE__*/ _jsxs(Typography, {
                        fontSize: "16px",
                        fontWeight: "500",
                        mt: 2,
                        children: [
                            t("pricingDescriptionTrialPrefix"),
                            " ",
                            /*#__PURE__*/ _jsx("strong", {
                                children: t("pricingDescriptionTrialBold")
                            }),
                            " ",
                            t("pricingDescriptionTrialSuffix")
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        fontSize: "16px",
                        fontWeight: "500",
                        mt: 2,
                        children: t("pricingDescriptionSwitching")
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        fontSize: "16px",
                        fontWeight: "500",
                        mt: 2,
                        children: t("pricingDescriptionDiscount")
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Box, {
                display: "flex",
                sx: {
                    maxWidth: "480px",
                    width: "100%",
                    paddingBottom: "20px",
                    "@media (min-width: 480px)": {
                        maxWidth: "364px"
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        sx: {
                            width: "50%",
                            borderRadius: "12px 0 0 12px",
                            background: isPerMonth ? "#FF9F24" : "rgba(116, 179, 255, 0.40)",
                            padding: "15px 25px",
                            justifyContent: "center",
                            alignItems: "center"
                        },
                        onClick: ()=>setIsPerMonth(true),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            sx: {
                                textTransform: "uppercase",
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#fff"
                            },
                            children: t("perMonth")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        sx: {
                            width: "50%",
                            borderRadius: "0 12px 12px 0",
                            background: !isPerMonth ? "#FF9F24" : "rgba(116, 179, 255, 0.40)",
                            padding: "15px 25px",
                            justifyContent: "center",
                            alignItems: "center"
                        },
                        onClick: ()=>setIsPerMonth(false),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            sx: {
                                textTransform: "uppercase",
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#fff"
                            },
                            children: t("perYear")
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Box, {
                sx: {
                    display: "flex",
                    flexWrap: mode === "account" && isDesktop ? "nowrap" : "wrap",
                    justifyContent: "center",
                    gap: "20px",
                    padding: "20px"
                },
                children: visiblePlans.map((plan)=>/*#__PURE__*/ _jsx(PricingCard, {
                        plan: plan,
                        // isPopular={plan.planName === "Package M"}
                        isCurrentPlan: showCurrentPlan && (effectiveCurrentPlan === FREE_PLAN ? plan.planName === effectiveCurrentPlan : plan.planName === effectiveCurrentPlan && isCurrentPlanDuration(currentDuration, plan.duration)),
                        onClick: onPlanClick
                    }, plan.id))
            }),
            /*#__PURE__*/ _jsxs(Box, {
                sx: {
                    maxWidth: "800px",
                    textAlign: "left",
                    padding: "20px"
                },
                children: [
                    /*#__PURE__*/ _jsxs(Box, {
                        sx: {
                            display: "flex",
                            alignItems: "flex-start"
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                fontSize: "14px",
                                fontWeight: "500",
                                sx: {
                                    minWidth: "24px"
                                },
                                children: /*#__PURE__*/ _jsx("strong", {
                                    children: "*"
                                })
                            }),
                            /*#__PURE__*/ _jsxs(Box, {
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        children: t("cancellationInfoTitle")
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        children: t("cancellationInfoMonthly")
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        children: t("cancellationInfoYearly")
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Box, {
                        sx: {
                            display: "flex",
                            alignItems: "flex-start",
                            mt: 2
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                fontSize: "14px",
                                fontWeight: "500",
                                sx: {
                                    minWidth: "24px"
                                },
                                children: /*#__PURE__*/ _jsx("strong", {
                                    children: "**"
                                })
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                fontSize: "14px",
                                fontWeight: "500",
                                children: [
                                    t("discountCodeInfoPrefix"),
                                    " ",
                                    /*#__PURE__*/ _jsx("strong", {
                                        children: "DISCOUNT20"
                                    }),
                                    " ",
                                    t("discountCodeInfoSuffix")
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Box, {
                        sx: {
                            display: "flex",
                            alignItems: "flex-start",
                            mt: 2
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                fontSize: "14px",
                                fontWeight: "500",
                                sx: {
                                    minWidth: "24px"
                                },
                                children: /*#__PURE__*/ _jsx("strong", {
                                    children: "***"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                fontSize: "14px",
                                fontWeight: "500",
                                children: t("unusedInvoicesInfo")
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
